import { ReactNode, useContext } from 'react';
import { APIProviderContext } from '@vis.gl/react-google-maps';

import { MapIdleEvent, MapSearch, ViewState } from '@atom/types/map';

import { DrawingConfig, EditingConfig } from './drawing/drawingTypes';
import AtomMapProvider from './AtomMapProvider';
import AtomMapView from './AtomMapView';
import { RecenterConfig } from './RecenterListener';
import { TaskFrameConfig } from './TaskBoundingBoxFramer';

export type AtomMapProps = {
  initialViewState: ViewState;
  onIdle?: (view: MapIdleEvent) => void;
  onSearchChange?: (search: MapSearch) => void;
  search?: 'plain' | 'editable' | 'none';
  showLayerControls?: boolean;
  showStyleControls?: boolean;
  showStreetView?: boolean;
  /** Google's camera control (tilt/rotate). */
  showTiltControls?: boolean;
  /** Shows a spinner to the left of the search bar while map data loads. */
  loading?: boolean;
  /** Enable feature editing. */
  editing?: EditingConfig;
  /** Enable the drawing toolbar + measurement readout (region/grab/measure). */
  drawing?: DrawingConfig;
  /** Pan the camera to a coordinate. */
  recenter?: RecenterConfig;
  /** Frame the camera to a task's bounding box (or a single location). */
  frameTask?: TaskFrameConfig;
  /** View-specific controls injected into the toolbar row. */
  customToolbars?: ReactNode;
  /** Keep every toolbar control on one row for narrow embeddings. */
  compactToolbars?: boolean;
  children?: ReactNode;
};

const AtomMap = (props: AtomMapProps) => {
  const insideProvider = useContext(APIProviderContext) !== null;

  if (insideProvider) {
    return <AtomMapView {...props} />;
  }

  return (
    <AtomMapProvider>
      <AtomMapView {...props} />
    </AtomMapProvider>
  );
};

export default AtomMap;
